/**
 * "Are you sure?" as a promise, in the app's own look.
 *
 * Body-level and imperative, like the toast: a question can come from anywhere
 * (the header, a menu, a keyboard shortcut) and should not need a component
 * mounted in advance to ask it. `window.confirm` would do the job, but it is
 * drawn by the browser - unstyled, unthemed, and it freezes the page - so this
 * builds the same small card the share and save dialogs use.
 *
 * Resolves `true` for the confirming button or Enter, `false` for Cancel,
 * Escape or a click on the scrim. Focus starts on Cancel: the safe answer is
 * the one a stray Enter or Space should land on only when it was meant.
 *
 * `confirmWithText` asks the same question with a text box under the message
 * (a reason, a note) and resolves to what was typed, or `null` for Cancel.
 * There focus starts in the box, and Enter there is a new line, not an answer.
 */

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  /** Styles the confirming button as a warning - for actions that discard work. */
  destructive?: boolean;
}

export interface TextFieldOptions {
  label: string;
  placeholder?: string;
  maxLength?: number;
}

let open: Promise<string | null> | null = null;

export async function confirmAction(options: ConfirmOptions): Promise<boolean> {
  return (await ask(options, null)) !== null;
}

/** The question with a text box; resolves to the text (possibly empty), or `null` for Cancel. */
export function confirmWithText(options: ConfirmOptions & { field: TextFieldOptions }): Promise<string | null> {
  return ask(options, options.field);
}

function ask(options: ConfirmOptions, field: TextFieldOptions | null): Promise<string | null> {
  // One question at a time: a second click on whatever asked gets the same answer.
  if (open) return open;

  open = new Promise<string | null>((resolve) => {
    const previousFocus = document.activeElement as HTMLElement | null;

    const scrim = document.createElement('div');
    scrim.className = 'confirm-scrim';

    const card = document.createElement('div');
    card.className = 'confirm-dialog';
    card.setAttribute('role', 'alertdialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-labelledby', 'confirm-title');
    card.setAttribute('aria-describedby', 'confirm-message');

    const title = document.createElement('h2');
    title.id = 'confirm-title';
    title.className = 'confirm-title';
    title.textContent = options.title;

    const message = document.createElement('p');
    message.id = 'confirm-message';
    message.className = 'confirm-message';
    message.textContent = options.message;

    const actions = document.createElement('div');
    actions.className = 'confirm-actions';

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'confirm-btn';
    cancel.textContent = options.cancelLabel ?? 'Cancel';

    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = `confirm-btn is-primary ${options.destructive ? 'is-destructive' : ''}`;
    confirm.textContent = options.confirmLabel;

    actions.append(cancel, confirm);
    card.append(title, message);
    let box: HTMLTextAreaElement | null = null;
    if (field) {
      const label = document.createElement('label');
      label.className = 'confirm-field';
      const caption = document.createElement('span');
      caption.textContent = field.label;
      box = document.createElement('textarea');
      box.rows = 3;
      if (field.placeholder) box.placeholder = field.placeholder;
      if (field.maxLength) box.maxLength = field.maxLength;
      label.append(caption, box);
      card.append(label);
    }
    card.append(actions);
    document.body.append(scrim, card);
    const stops: HTMLElement[] = box ? [box, cancel, confirm] : [cancel, confirm];

    const finish = (answer: boolean): void => {
      document.removeEventListener('keydown', onKey, true);
      scrim.remove();
      card.remove();
      open = null;
      previousFocus?.focus?.();
      resolve(answer ? (box?.value.trim() ?? '') : null);
    };

    // Captured, so Escape and Enter answer this dialog rather than reaching
    // whatever shortcut is listening behind it.
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        finish(false);
      } else if (e.key === 'Enter' && document.activeElement !== cancel && document.activeElement !== box) {
        e.preventDefault();
        e.stopPropagation();
        finish(true);
      } else if (e.key === 'Tab') {
        // Keep focus inside the dialog.
        e.preventDefault();
        const at = stops.indexOf(document.activeElement as HTMLElement);
        stops[(at + (e.shiftKey ? stops.length - 1 : 1)) % stops.length]!.focus();
      }
    };

    cancel.addEventListener('click', () => finish(false));
    confirm.addEventListener('click', () => finish(true));
    scrim.addEventListener('click', () => finish(false));
    document.addEventListener('keydown', onKey, true);
    (box ?? cancel).focus();
  });
  return open;
}
